import { AfterViewInit, Component, effect, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { CartStore } from '@apps/mtjr/customers/app/cart/cart.store';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { ApiService } from '@libs/core/base-config';
import { AuthStateStore } from '@libs/core/lib/auth/auth-state.store';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { PWAInstallStore } from '@libs/core/lib/pwa/pwa-install.store';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { AppUpdatesService } from '@libs/core/lib/utils/app-updates.service';
import { ThemeStore } from '@libs/core/lib/utils/theme/theme.store';
import { LanguageSwitcherStore } from '@libs/mtjr/shared/i18n/language-switcher.store';
import { NotificationsStore } from '@libs/mtjr/shared/notifications/notifications.store';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { WalletStore } from '@libs/mtjr/shared/wallet/wallet.store';
import { TranslateService } from '@ngx-translate/core';
import { register } from 'swiper/element/bundle';

// register Swiper elements
register();
@Component({
  selector: 'mac-root',
  template: `
    <ion-app>
      <ion-router-outlet />
    </ion-app>
  `,
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements AfterViewInit {
  themeStore = inject(ThemeStore);
  authStore = inject(AuthStateStore);
  cartStore = inject(CartStore);
  router = inject(Router);
  userStore = inject(UserStore);
  languageSwitcherStore = inject(LanguageSwitcherStore);
  walletStore = inject(WalletStore);
  apiService = inject(ApiService);
  eventBus = inject(NgEventBus);
  alerts = inject(AppAlerts);
  translate = inject(TranslateService);
  pwaInstallStore = inject(PWAInstallStore);
  ngZone = inject(NgZone);
  notificationsStore = inject(NotificationsStore);
  appUpdatesService = inject(AppUpdatesService);
  swUpdate = inject(SwUpdate);

  constructor() {
    this.themeStore.initTheme();
    this.cartStore.init();
    this.userStore.fetchUserIfNeeded();
    this.languageSwitcherStore.init();
    this.pwaInstallStore.init();
    this.appUpdatesService.init();
    this.notificationsStore.registerFirebaseMessaging();

    effect(async () => {
      if (this.authStore.isAuthenticated()) {
        this.userStore.fetchUserIfNeeded();
        this.walletStore.refreshBalance();
      } else {
        this.userStore.resetState();
      }
    });

    this.translate.onTranslationChange.subscribe(() => {
      document.title = this.translate.instant('APP_NAME');
    });
  }

  ngAfterViewInit(): void {
    this.notificationsStore.onBackgroundMessage((payload) => {
      this.handleNotificationMessage(payload.data);
    });

    this.notificationsStore.onMessage((payload) => {
      this.handleNotificationMessage(payload.data);
    });
  }

  handleNotificationMessage(payload?: { [key: string]: string }) {
    console.log('Notification payload:', payload);
    const type = payload?.['type'];
    if (type === 'order.chat') {
      const orderId = payload?.['orderId'];
      const subOrderId = payload?.['subOrderId'];
      if (orderId && subOrderId) {
        this.handleOrderChatEvent({ orderId, subOrderId });
      }
    } else if (type === 'order.status') {
      const orderId = payload?.['orderId'];
      if (orderId) {
        this.eventBus.cast('order:fetch', { orderId });
      }
    } else if (type === 'order.statusAndWallet') {
      const orderId = payload?.['orderId'];
      if (orderId) {
        this.eventBus.cast('order:fetch', { orderId });
        this.eventBus.cast('wallet:fetch');
      }
    } else if (type === 'wallet.credit' || type === 'wallet.debit') {
      if (type === 'wallet.credit') {
        this.alerts.successSnackbarTR('WALLET.BALANCE_UPDATED');
      }
      this.eventBus.cast('wallet:fetch');
    }
  }

  private handleOrderChatEvent(data: { orderId: string; subOrderId: string }) {
    const orderId = data?.orderId;
    const subOrderId = data?.subOrderId;
    if (orderId && subOrderId) {
      const url = this.router.parseUrl(this.router.url);
      const currentUrl = url.toString();
      if (currentUrl.includes('/orders/' + orderId)) {
        // page is opened
        const openedChatId = url.queryParams['order-chat'];
        if (subOrderId !== openedChatId) {
          // chat is not opened
          this.openOrderChat(orderId, subOrderId);
        }
      } else {
        // page is not opened
        this.openOrderChat(orderId, subOrderId);
      }
      this.eventBus.cast('order:fetch-chat', { orderId, subOrderId });
    }
  }

  openOrderChat(orderId: string, subOrderId: string) {
    this.alerts.snackbarTR('ORDERS.INCOMING_CHAT', {
      position: 'top',
      duration: 5000,
      buttons: [
        {
          text: this.translate.instant('ORDERS.OPEN_CHAT'),
          handler: () => {
            this.router.navigate(['/', 'orders', orderId], {
              queryParams: { 'order-chat': subOrderId },
              queryParamsHandling: 'merge',
            });
          },
        },
      ],
    });
  }
}
